import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import FormHeader from 'components/FormHeader';
import { FormRow, UploadHint, Note } from 'styles/CommonStyles';
import FormFileUpload from 'components/FormFileUpload';
import FormTextInput from 'components/FormTextInput';
import FormDatePicker from 'components/FormDatePicker';
import FormSubmit from 'components/FormSubmit';
import FormTracker from 'components/FormTracker';
import useSubmitActions from 'hooks/submit';
import useUploadActions from 'hooks/upload';
import { FormIdentifier } from 'lib/constants';
import { normalizePhoneNumber } from 'lib/utils';
import { patterns } from 'lib/validation';
import { showSubmitSuccessState } from 'state/atoms/submit';
import { acceptedFiles } from 'lib/constants';
const SCRAForm = () => {
  const { i18n, t } = useTranslation();
  const { submitForm } = useSubmitActions();
  const { uploadFiles } = useUploadActions();
  const formMethods = useForm();
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);
  const watchedAccountId = formMethods.watch('accountId');

  const onSubmit = useCallback(
    (data: any) => {
      const { documents, phoneNumber, ...otherData } = data;
      const formData = {
        formType: FormIdentifier.SCRA,
        phoneNumber: normalizePhoneNumber(phoneNumber),
        ...otherData,
      };
      const formSessionUuid = uuidv4();

      return uploadFiles(formSessionUuid, 'compliance', documents)
        .then((uploadedDocs) =>
          submitForm(
            formSessionUuid,
            { ...formData, documents: uploadedDocs },
            i18n.language
          )
        )
        .then(() => setShowSuccess(true));
    },
    [i18n.language, setShowSuccess, submitForm, uploadFiles]
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormHeader
          description={t('forms.scra.description')}
          title={t('forms.scra.name')}
        />
        <FormRow>
          <FormTextInput
            label={t<string>('labels.name')}
            name="name"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.phoneNumber')}
            name="phoneNumber"
            rules={{
              pattern: {
                message: t<string>('validation.phoneNumberFormat'),
                value: patterns.phoneNumber,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.emailAddress')}
            name="email"
            rules={{
              pattern: {
                message: t<string>('validation.emailFormat'),
                value: patterns.emailAddress,
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormDatePicker
            label={t<string>('labels.activeDutyStartDate')}
            name="activeDutyStartDate"
            rules={{ required: t<string>('validation.required') }}
          />
        </FormRow>
        <FormRow>
          <div>
            {t('labels.loadIdOrAccountIdRequired')}
            <FormRow>
              <FormTextInput
                label={t<string>('labels.accountId')}
                name="accountId"
                rules={{
                  pattern: {
                    message: t<string>('validation.accountNumberFormat'),
                    value: patterns.applicationId,
                  },
                  validate: () => {
                    const { accountId, loanId } = formMethods.getValues();
                    if (!accountId && !loanId) {
                      return t<string>('validation.loadIdOrAccountIdRequired');
                    }
                  },
                }}
                onChange={async (val, e) => {
                  formMethods.setValue('accountId', e.target.value);
                  if (formMethods.formState.isSubmitted) {
                    await formMethods.trigger('loanId');
                    await formMethods.trigger('accountId');
                  }
                }}
              />
              <FormTextInput
                label={t<string>('labels.loanId')}
                name="loanId"
                rules={{
                  pattern: {
                    message: t<string>('validation.loanIdFormat'),
                    value: patterns.loanId,
                  },
                  validate: async () => {
                    if (
                      !formMethods.getValues('accountId') &&
                      !formMethods.getValues('loanId')
                    ) {
                      return ' '; // Prevent label from being highlighted red
                    }
                  },
                }}
                onChange={async (val, e) => {
                  formMethods.setValue('loanId', e.target.value);
                  if (formMethods.formState.isSubmitted) {
                    await formMethods.trigger('loanId');
                    await formMethods.trigger('accountId');
                  }
                }}
              />
            </FormRow>
            {watchedAccountId && <Note>{t('labels.scraAccountIDNote')}</Note>}
          </div>
        </FormRow>
        <FormRow>
          <div>
            <FormFileUpload
              acceptTypes={acceptedFiles}
              ariaLabel={t('labels.uploadDocument')}
              label={
                <>
                  {t('labels.uploadDocument')}
                  <UploadHint>
                    <Trans i18nKey="labels.uploadActiveService">
                      Please upload proof of active duty status. Make sure to
                      <b>REDACT SSN</b>.,
                    </Trans>
                  </UploadHint>
                </>
              }
              name="documents"
              maxFiles={20}
              rules={{
                required: t<string>('validation.uploadDocumentRequired'),
              }}
            />
          </div>
        </FormRow>
        <FormSubmit formType={FormIdentifier.SCRA} />
        <FormTracker formType={FormIdentifier.SCRA} />
      </form>
    </FormProvider>
  );
};

export default SCRAForm;
